<template>
    <div>
        <v-row no-gutters>
            <v-col class="d-flex align-center col-auto pr-3 title">
                {{ title }}
            </v-col>
            <v-col class="d-flex align-center">
                <v-divider />
            </v-col>
        </v-row>
        <v-form v-model="valid">
            <v-row no-gutters style="border: 1px #ededed solid; border-radius: 15px" class="pa-2">
                <v-col cols="4" class="mt-2 pr-2" v-if="sepas.length">
                    <v-select-filled
                        v-model="selectedSepa"
                        :items="sepas"
                        item-value="id"
                        :label="title"
                        :mandatory="true"
                        hide-details="auto"
                        return-object
                        @update:model-value="onDocumentChanged"
                        :readonly="readonly"
                        density="compact"
                        :item-title="(item) => item.displayName"
                    />
                </v-col>
                <v-col :cols="sepas.length ? 4 : 6" class="mt-2">
                    <v-text-field-filled
                        v-model="selectedSepa.displayName"
                        label="Nom"
                        :rules="[rules.notEmpty]"
                        hide-details="auto"
                        :readonly="readonly"
                        density="compact"
                    />
                </v-col>
                <v-col :cols="sepas.length ? 4 : 6" class="pl-2 mt-2">
                    <oss-date-picker-filled
                        v-model="selectedSepa.date"
                        label="Date"
                        :textFieldReadOnly="false"
                        :mandatory="true"
                        hide-details="auto"
                        :readonly="readonly"
                    />
                </v-col>
                <v-col cols="6" class="mt-2">
                    <v-text-field-filled
                        v-model="company.name"
                        label="Nom de la société"
                        hide-details
                        :rules="[rules.notEmpty]"
                        readonly
                        density="compact"
                    />
                </v-col>
                <v-col cols="6" class="pl-2 mt-2">
                    <v-text-field-filled
                        v-model="company.siret"
                        label="Siret"
                        @input="onInputSetTruncatedSiret(company.siret)"
                        hide-details
                        :rules="[rules.notEmpty, rules.checkSiret]"
                        readonly
                        density="compact"
                    />
                </v-col>
                <v-col :cols="type === 'directDebit' ? 6 : 4" class="mt-2">
                    <v-select-filled
                        v-model="bankAccountId"
                        :items="bankAccounts"
                        item-value="id"
                        :label="type === 'directDebit' ? 'Compte à créditer' : 'Compte à débiter'"
                        :mandatory="true"
                        hide-details="auto"
                        :rules="[(v) => !!v || 'Compte bancaire requis']"
                        :readonly="readonly"
                        density="compact"
                        :item-title="(item) => item.name"
                    />
                </v-col>
                <v-col :cols="type === 'directDebit' ? 6 : 4" class="pl-2 mt-2" v-if="bankAccount">
                    <v-text-field-filled
                        v-model="bankAccount.iban"
                        label="IBAN"
                        outlined
                        :rules="[rules.notEmpty, rules.checkIBAN]"
                        @change="onIbanChanged(bankAccount)"
                        @input="onInputSetTruncatedIban(bankAccount.iban)"
                        :readonly="readonly"
                        density="compact"
                    />
                </v-col>
                <v-col
                    :cols="type === 'directDebit' ? 6 : 4"
                    :class="type === 'directDebit' ? 'mt-2' : 'pl-2 mt-2'"
                    v-if="bankAccount"
                >
                    <v-text-field-filled
                        v-model="bankAccount.bic"
                        label="BIC"
                        outlined
                        :rules="[rules.notEmpty, rules.checkBIC]"
                        :readonly="readonly"
                        density="compact"
                    />
                </v-col>
                <v-col cols="6" class="pl-2 mt-2" v-if="bankAccount && type === 'directDebit'">
                    <v-text-field-filled
                        v-model="bankAccount.creditorIdentifier"
                        label="ICS"
                        outlined
                        :rules="[rules.notEmpty, rules.checkICS]"
                        :readonly="readonly"
                        density="compact"
                    />
                </v-col>
            </v-row>

            <v-row
                style="border: 1px #ededed solid; border-radius: 15px"
                class="mt-2 pa-2"
                no-gutters
                v-if="transactions.length"
            >
                <v-col cols="12">
                    <oss-data-table :items="transactions" :headers="headers" :show-select="false">
                        <template v-slot:item="props">
                            <tr>
                                <!-- // **** date ******* -->
                                <td>
                                    {{ props.item.reason }}
                                </td>
                                <td>
                                    {{ props.item.thirdParty }}
                                </td>
                                <td>
                                    <v-text-field-filled
                                        v-model="props.item.bankAccount.iban"
                                        placeholder="IBAN"
                                        hide-details="auto"
                                        :rules="[rules.notEmpty, rules.checkIBAN]"
                                        @change="onIbanChanged(props.item.bankAccount)"
                                        v-if="props.item.bankAccount"
                                        :readonly="readonly"
                                        density="compact"
                                    />
                                </td>
                                <td>
                                    <v-text-field-filled
                                        v-model="props.item.bankAccount.bic"
                                        placeholder="BIC"
                                        hide-details="auto"
                                        :rules="[rules.notEmpty, rules.checkBIC]"
                                        v-if="props.item.bankAccount"
                                        :readonly="readonly"
                                        density="compact"
                                    />
                                </td>
                                <td v-if="type === 'directDebit'">
                                    <v-text-field-filled
                                        v-model="props.item.bankAccount.mandate.id"
                                        placeholder="RUM"
                                        hide-details="auto"
                                        :rules="[rules.notEmpty]"
                                        :readonly="readonly"
                                        v-if="
                                            props.item.bankAccount && props.item.bankAccount.mandate
                                        "
                                        density="compact"
                                    />
                                </td>
                                <td v-if="type === 'directDebit'">
                                    <oss-date-picker-filled
                                        v-model="props.item.bankAccount.mandate.date"
                                        hide-details
                                        :textFieldReadOnly="false"
                                        :outlined="false"
                                        :no-menu="true"
                                        :readonly="readonly"
                                        v-if="
                                            props.item.bankAccount && props.item.bankAccount.mandate
                                        "
                                    />
                                </td>
                                <td>
                                    <oss-text-field
                                        v-model="props.item.paymentAmount"
                                        suffix="&#x20ac;"
                                        placeholder="Montant"
                                        dense
                                        single-line
                                        :outlined="false"
                                        hide-details="auto"
                                        :readonly="readonly"
                                        :rules="[
                                            (v) =>
                                                (!!v && parseFloat(v) > 0) ||
                                                $t('settings.positiveNumber'),
                                        ]"
                                    />
                                </td>
                                <td class="text-center">
                                    <v-btn-trash
                                        @click="removeTransaction(props.item)"
                                        :disabled="readonly"
                                    />
                                </td>
                            </tr>
                        </template>
                        <template v-slot:body.append>
                            <tr class="x-dense">
                                <td class="text-right" :colspan="type === 'directDebit' ? 6 : 4">
                                    <strong>Total</strong>
                                </td>
                                <td class="text-right">
                                    <div>
                                        <strong>{{
                                            newCommonFormatCurrency(total, undefined)
                                        }}</strong>
                                    </div>
                                </td>
                                <td />
                            </tr>
                        </template>
                    </oss-data-table>
                </v-col>
                <v-col
                    cols="12"
                    class="d-flex justify-end align-center py-3"
                    v-if="mode === 'Embedded'"
                >
                    <v-btn
                        size="small"
                        @click="updateSepa()"
                        :loading="loading"
                        :disabled="!globalValid"
                        v-if="!validated"
                        >Modifier</v-btn
                    >
                    <v-btn
                        size="small"
                        @click="updateSepa('VALIDATED')"
                        class="ml-2"
                        :loading="loading"
                        :disabled="!globalValid || readonly"
                        >{{ validated ? "Validé" : "Valider"
                        }}<v-icon v-show="validated">$check</v-icon></v-btn
                    >
                </v-col>
            </v-row>
        </v-form>
    </div>
</template>

<script lang="ts">
import moment from "moment-timezone";
import gql from "graphql-tag";
import _ from "lodash";
import { truncateInput } from "@/helpers/formInput.helper";

import { getUserDisplayName } from "../../services/userServices";
import {
    checkIBAN,
    checkBIC,
    checkICS,
    getBICFromIBAN,
    getBankNameFromIban,
    onIbanChanged,
} from "../../../../backend/src/common/bankAccount/bankAccountServices";
import { Apollo } from "@/decorators";
import OssDataTable from "../ossComponents/ossDataTable2.vue";

import BANK_ACCOUNTS from "../../graphql/bank/BankAccounts.gql";
import CREATE_DOCUMENT from "../../graphql/document/CreateDocument.gql";
import UPDATE_DOCUMENT from "../../graphql/document/UpdateDocument.gql";
import THIRDPARTY_COMPANIES_DATA from "../../graphql/invoice/ThirdPartyCompaniesData.gql";
import DELETE_PAYMENT_LINK from "../../graphql/paymentLinks/DeletePaymentLink.gql";
import SEPA_TPC_ACCOUNTINGLINES from "../../graphql/pay/SepaTPCAccountingLines.gql";
import SEPA_USER_ACCOUNTINGLINES from "../../graphql/pay/SepaUserAccountingLines.gql";
import UPDATE_BANK_ACCOUNT from "../../graphql/bankAccount/UpdateBankAccount.gql";
import { isSiret } from "../../../../backend/src/common/company/siretSirenServices";
import { newCommonFormatCurrency } from "../../../../backend/src/common/services/mathServices";
import { getDBFormattedDate } from "@/services/dateServices";
import { Component, Prop, Vue, toNative } from "vue-facing-decorator";

class Transaction {
    id: string;
    wording: string;
    invoiceNumber: string;

    accountableAccount: any;
    user: any;
    thirdPartyCompany: {
        id: string;
        name: string;
        thirdPartyCompanyData: any;
    };

    amountSigned: number = 0.0;
    paymentAmount: number = 0.0;
    editable: boolean;

    accountingLineId_: string;
    reason_: string;
    thirdPartyCompanyId_: string;
    userId_: string;
    thirdParty_: string;
    accountableAccountName_: string;
    // associatedPaymentLink_:string

    bankAccount = {
        iban: "",
        bic: "",
        mandate: {
            id: "",
            date: "",
        },
    };

    bankAccounts = [];

    constructor(editable) {
        this.editable = editable;
    }

    get thirdParty() {
        if (this.user) {
            return getUserDisplayName(this.user);
        }
        if (this.thirdPartyCompany) {
            return this.thirdPartyCompany.name;
        }
        return "XXX";
    }

    set thirdParty(value) {
        this.thirdParty_ = value;
    }

    get reason() {
        if (this.reason_) {
            return this.reason_;
        }
        let reason = this.wording;
        if (this.invoiceNumber) {
            reason += " - " + this.invoiceNumber;
        }
        return reason;
    }

    set reason(value) {
        this.reason_ = value;
    }

    get userId() {
        if (this.userId_) {
            return this.userId_;
        }
        if (this.user) {
            return this.user.id;
        }
        return undefined;
    }

    set userId(value) {
        this.userId_ = value;
    }

    get thirdPartyCompanyId() {
        if (this.thirdPartyCompanyId_) {
            return this.thirdPartyCompanyId_;
        }
        if (this.thirdPartyCompany) {
            return this.thirdPartyCompany.id;
        }
        return undefined;
    }

    set thirdPartyCompanyId(value) {
        this.thirdPartyCompanyId_ = value;
    }

    get accountableAccountName() {
        if (this.accountableAccountName_) {
            return this.accountableAccountName_;
        }
        if (this.accountableAccount) {
            return this.accountableAccount.name;
        }
        return "";
    }

    set accountableAccountName(value) {
        this.accountableAccountName_ = value;
    }

    get accountingLineId() {
        if (this.accountingLineId_) {
            return this.accountingLineId_;
        }
        return this.id;
    }

    set accountingLineId(value) {
        this.accountingLineId_ = value;
    }
}

@Component({
    components: {
        OssDataTable,
    },
    emits: ["onUpdated"],
})
export class Sepa extends Vue {
    @Prop({ default: "Dialog" }) readonly mode: string;
    @Prop({ default: "" }) readonly companyId: string;

    @Prop({ default: null }) readonly filteredThirdPartyCompanies: any;

    @Prop({ default: null }) startDate: string;
    @Prop({ default: null }) endDate: string;
    @Prop({ default: null }) accountableAccount: string;

    @Prop({ default: null }) document: any;

    @Prop({ default: null }) accountingLines: any;
    @Prop({ default: null }) actions: any;

    bankAccountId = "";

    valid = false;
    loading = false;

    newCommonFormatCurrency(value, options = undefined) {
        return newCommonFormatCurrency(value, undefined);
    }

    get headers() {
        const result = [
            { text: "Motif", value: "motif", sortable: false, align: "left" },
            {
                text: this.type === "directDebit" ? "Client" : "Fournisseur|Employé",
                value: "thirdParty",
                sortable: false,
                align: "left",
            },
            { text: "IBAN", value: "iban", sortable: false, align: "left" },
            { text: "BIC", value: "bic", sortable: false, align: "left" },
        ];
        if (this.type === "directDebit") {
            result.push({ text: "RUM", value: "mandateId", sortable: false, align: "left" });
            result.push({
                text: "Date du mandat",
                value: "mandateDate",
                sortable: false,
                align: "left",
            });
        }
        result.push({ text: "Montant", value: "paymentAmount", sortable: false, align: "right" });
        result.push({ text: "", value: "", sortable: false, align: "right" });

        return result;
    }

    get company() {
        return this.$store.getters.getCompany(this.companyId);
    }

    get globalValid() {
        if (!this.valid) return false;
        if (!this.type) return false;
        return true;
    }

    get bankAccount(): any {
        return this.bankAccounts.find((fba) => fba.id === this.bankAccountId) || null;
    }

    get validated() {
        return this.document?.status === "VALIDATED";
    }

    get readonly() {
        return false;
        // return this.validated
    }

    get accountableAccountName() {
        if (this.accountableAccount) {
            return this.accountableAccount;
        }
        let accountableAccountName;
        for (const transaction of this.transactions) {
            if (!transaction.accountableAccountName) {
                return "";
            }
            if (
                accountableAccountName &&
                accountableAccountName !== transaction.accountableAccountName
            ) {
                return "";
            }
            accountableAccountName = transaction.accountableAccountName;
        }
        return accountableAccountName;
    }

    get withTPC() {
        if (!this.accountableAccountName) {
            return false;
        }
        if (
            this.accountableAccountName.startsWith("401") ||
            this.accountableAccountName.startsWith("411")
        ) {
            return true;
        }
        return false;
    }

    get withUser() {
        if (!this.accountableAccountName) {
            return false;
        }
        if (
            this.accountableAccountName.startsWith("421") ||
            this.accountableAccountName.startsWith("425") ||
            this.accountableAccountName.startsWith("455") ||
            this.accountableAccountName.startsWith("108") ||
            this.accountableAccountName.startsWith("467")
        ) {
            return true;
        }
        return false;
    }

    get type() {
        // debugger;
        if (!this.accountableAccountName) {
            return "";
        }
        if (this.accountableAccountName.startsWith("411")) {
            return "directDebit";
        } else if (
            this.accountableAccountName.startsWith("401") ||
            this.accountableAccountName.startsWith("421") ||
            this.accountableAccountName.startsWith("425") ||
            this.accountableAccountName.startsWith("455") ||
            this.accountableAccountName.startsWith("108") ||
            this.accountableAccountName.startsWith("467")
        ) {
            return "transfer";
        } else {
            console.error(this.accountableAccountName, "not supported");
            return "";
        }
    }

    get title() {
        if (this.type === "transfer") {
            return "Ordre de virement";
        }
        if (this.type === "directDebit") {
            return "Ordre de prélèvement";
        }
        return "Ordre inconnu";
    }

    get displayName() {
        return this.title + " du " + moment().format("DD/MM/YYYY");
    }

    // cleanDescription() {
    //     let description = this.description
    //     for (const c in this.forbiddenCharacters) {
    //         const regexp = new RegExp(c, "g")
    //         description = description.replace(regexp, this.forbiddenCharacters[c])
    //     }
    //     return description
    // }

    newTransactions: Transaction[] = [];
    transactions: Transaction[] = [];

    onInputSetTruncatedSiret(siret) {
        this.company.siret = truncateInput(siret, 14);
    }

    onInputSetTruncatedIban(iban) {
        this.bankAccount.iban = truncateInput(iban, 34);
    }

    onDocumentChanged() {
        this.transactions = [];
        if (this.selectedSepa?.data?.sepa?.transactions?.length) {
            for (const sepaTransaction of this.selectedSepa.data.sepa.transactions) {
                //  this.transactions.push(Transaction.instanciateFromSepaTransaction(sepaTransaction))
                this.transactions.push(Object.assign(new Transaction(false), sepaTransaction, {}));
            }
        }

        this.transactions.push(...this.newTransactions);
        this.updateSelectedBankAccoundId();
        this.updateThirdPartyCompaniesBankAccounts();
        this.updateUserBankAccounts();
    }

    beforeMount() {
        if (this.actions) {
            this.actions.action.title = "Valider";
            this.actions.action.function = this.updateSepa;
            this.actions.action.loading = () => {
                return this.loading;
            };
            this.actions.action.disabled = () => {
                return !this.globalValid;
            };
        }
        if (this.accountingLines) {
            for (const al of this.accountingLines) {
                this.newTransactions.push(
                    Object.assign(new Transaction(true), al, {
                        paymentAmount: Math.abs(al.amountSigned),
                    }),
                );
            }
        }
        if (this.document) {
            this.selectedSepa = this.document;
            if (this.selectedSepa?.data?.sepa) {
                // if (this.selectedSepa.data.sepa.bankAccount) {
                //     this.bankAccount = this.selectedSepa.data.sepa.bankAccount
                // } else {
                // }
            }
        }

        this.onDocumentChanged();
    }

    updateSelectedBankAccoundId() {
        //
        // bankAccountId
        //
        this.bankAccountId = "";
        const searchIban =
            this.selectedSepa.data?.sepa?.bankAccount?.iban || this.selectedSepa?.data?.sepa?.iban;
        if (searchIban) {
            for (const bankAccount of this.bankAccounts) {
                if (bankAccount.iban === searchIban) {
                    this.bankAccountId = bankAccount.id;
                    // found = true
                    break;
                }
            }
        }
        if (!this.bankAccountId) {
            for (const bankAccount of this.bankAccounts) {
                if (bankAccount.iban) {
                    this.bankAccountId = bankAccount.id;
                    // found = true
                    break;
                }
            }
        }
        if (!this.bankAccountId && this.bankAccounts.length) {
            this.bankAccountId = this.bankAccounts[0].id;
        }
    }

    @Apollo({
        query: BANK_ACCOUNTS,
        variables() {
            return {
                companyId: this.companyId,
                type: "BANK",
            };
        },
        fetchPolicy: "network-only",
        skip() {
            // debugger;
            return !this.companyId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data?.bankAccounts?.length) {
                    for (const bankAccount of data.bankAccounts) {
                        if (!bankAccount.iban) {
                            bankAccount.iban = "";
                        }
                        if (!bankAccount.bic) {
                            if (bankAccount.iban) {
                                bankAccount.bic = getBICFromIBAN(bankAccount.iban);
                            } else {
                                bankAccount.bic = "";
                            }
                        }
                        if (!bankAccount.name) {
                            bankAccount.name = getBankNameFromIban(bankAccount.name);
                        }
                    }

                    this.bankAccounts = data.bankAccounts;
                    this.updateSelectedBankAccoundId();
                    // debugger;
                } else {
                    this.bankAccounts = [];
                    this.bankAccountId = "";
                }
                this.originalBankAccounts = _.cloneDeep(this.bankAccounts);
            }
        },
    })
    bankAccounts = [];
    originalBankAccounts = [];

    get total() {
        if (!this.transactions?.length) {
            return 0;
        }
        let total = 0;
        for (const al of this.transactions) {
            total += al.paymentAmount;
        }
        return total;
    }

    get totalSigned() {
        if (!this.transactions?.length) {
            return 0;
        }
        let total = 0;
        for (const al of this.transactions) {
            total += al.amountSigned;
        }
        return total;
    }

    get thirdPartyCompanyWhere() {
        const where: any = {
            thirdPartyCompanyPaymentType: this.accountableAccount?.startsWith("411")
                ? "DIRECT_DEBIT"
                : undefined,
        };
        if (this.accountableAccount?.startsWith("411")) {
            where.thirdPartyCompanyPaymentType = "DIRECT_DEBIT";
        }
        if (this.filteredThirdPartyCompanies?.length) {
            where.id_in = this.filteredThirdPartyCompanies.map((ftpc) => ftpc.id);
        } else {
            where.id_not = "";
        }
        return where;
    }

    @Apollo({
        query: SEPA_TPC_ACCOUNTINGLINES,
        variables() {
            return {
                companyId: this.companyId,
                accountableAccount: this.accountableAccount,
                startDate: moment(this.startDate).utc(true).format(),
                endDate: moment(this.endDate).endOf("day").utc(true).format(),
                // thirdPartyCompany: !this.accountableAccount?.startsWith("411") ? {id_not:""} : {thirdPartyCompanyPaymentType: "DIRECT_DEBIT"}
                thirdPartyCompany: this.thirdPartyCompanyWhere,
            };
        },
        skip() {
            return (
                !this.companyId ||
                !this.accountableAccount ||
                !this.startDate ||
                !this.endDate ||
                !this.withTPC ||
                (this.filteredThirdPartyCompanies && !this.filteredThirdPartyCompanies.length)
            );
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                // debugger;
                if (data.accountingLines) {
                    const sepaTPCAccountingLines = data.accountingLines;
                    const transactions: Transaction[] = [];
                    for (const al of sepaTPCAccountingLines) {
                        const transaction: Transaction = Object.assign(
                            new Transaction(false),
                            al,
                            {},
                        );
                        transaction.paymentAmount = Math.abs(al.amountSigned);
                        if (al.document?.invoiceNumber) {
                            transaction.invoiceNumber = al.document?.invoiceNumber;
                        }
                        transactions.push(transaction);
                    }

                    transactions.sort((a: Transaction, b: Transaction) =>
                        a.thirdPartyCompany?.name < b.thirdPartyCompany?.name ? -1 : 1,
                    );

                    this.transactions = transactions;
                    this.sepaTPCAccountingLines = sepaTPCAccountingLines;
                } else {
                    this.sepaTPCAccountingLines = [];
                }
            }
        },
    })
    sepaTPCAccountingLines = [];

    @Apollo({
        query: SEPA_USER_ACCOUNTINGLINES,
        variables() {
            return {
                companyId: this.companyId,
                accountableAccount: this.accountableAccount,
                startDate: moment(this.startDate).utc(true).format(),
                endDate: moment(this.endDate).endOf("day").utc(true).format(),
            };
        },
        skip() {
            return (
                !this.companyId ||
                !this.accountableAccount ||
                !this.startDate ||
                !this.endDate ||
                !this.withUser
            );
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                // debugger;
                if (data.accountingLines) {
                    const sepaUserAccountingLines = data.accountingLines;
                    for (const al of sepaUserAccountingLines) {
                        const transaction: Transaction = Object.assign(
                            new Transaction(false),
                            al,
                            {},
                        );
                        transaction.paymentAmount = Math.abs(al.amountSigned);
                        if (al.document?.invoiceNumber) {
                            transaction.invoiceNumber = al.document?.invoiceNumber;
                        }
                        this.transactions.push(transaction);
                    }
                    this.sepaUserAccountingLines = sepaUserAccountingLines;
                } else {
                    this.sepaUserAccountingLines = [];
                }
            }
        },
    })
    sepaUserAccountingLines = [];

    @Apollo({
        query: gql`
            query ($companyId: ID!) {
                documents(
                    companyId: $companyId
                    where: {
                        company: { id: $companyId }
                        status_in: [ANALYSED, ANALYSED_OK]
                        type: SEPA
                    }
                    orderBy: createdAt_DESC
                ) {
                    id
                    date
                    displayName
                    data
                }
            }
        `,
        variables() {
            return {
                companyId: this.companyId,
            };
        },
        skip() {
            // debugger;
            return !this.companyId || this.document || !this.type;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                // debugger;
                const newSepa = JSON.parse(JSON.stringify(this.newSepa));
                newSepa.displayName = this.displayName;
                const documents = [];
                if (data?.documents?.length) {
                    for (const doc of data.documents) {
                        if (doc.data?.sepa?.type === this.type) {
                            documents.push(doc);
                        }
                    }
                }
                if (documents.length) {
                    const sepas = [];
                    newSepa.displayName += " (nouveau)";
                    sepas.push(newSepa);

                    sepas.push(...documents);

                    this.selectedSepa = data.documents[0];
                    this.sepas = sepas;
                    this.onDocumentChanged();
                } else {
                    this.selectedSepa = newSepa;
                    this.sepas = [];
                    this.onDocumentChanged();
                }
            }
        },
    })
    sepas = [];

    newSepa = {
        id: "",
        date: moment().format("YYYY-MM-DD"),
        displayName: this.title + " du " + moment().format("DD/MM/YYYY"),
        data: null,
    };

    selectedSepa = {
        id: "",
        date: moment().format("YYYY-MM-DD"),
        displayName: this.title + " du " + moment().format("DD/MM/YYYY"),
        data: null,
    };

    get userIds() {
        const userIds = [];
        for (const transaction of this.transactions) {
            if (transaction.userId) {
                userIds.push(transaction.userId);
            }
        }
        return userIds;
    }

    get thirdPartyCompanyIds() {
        const ids = [];
        for (const transaction of this.transactions) {
            if (transaction.thirdPartyCompanyId) {
                ids.push(transaction.thirdPartyCompanyId);
            }
        }
        return ids;
    }

    @Apollo({
        query: THIRDPARTY_COMPANIES_DATA,
        variables() {
            return {
                thirdPartyCompanyIds: this.thirdPartyCompanyIds,
            };
        },
        skip() {
            return !this.companyId || !this.thirdPartyCompanyIds?.length;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data?.companies?.length) {
                    for (const tpc of data.companies) {
                        const bankAccounts = tpc.thirdPartyCompanyData?.ibans?.length
                            ? tpc.thirdPartyCompanyData?.ibans
                            : tpc.ibans?.length
                              ? tpc.iban
                              : [];
                        if (!bankAccounts?.length) {
                            tpc.bankAccount = {
                                name: "",
                                iban: "",
                                bic: "",
                            };
                            if (this.type === "directDebit") {
                                tpc.bankAccount.mandate = {
                                    id: "",
                                    name: "",
                                };
                            }
                            tpc.bankAccounts = [];
                            tpc.bankAccounts.push(tpc.bankAccount);
                        } else {
                            for (const bankAccount of bankAccounts) {
                                if (!bankAccount.name) {
                                    bankAccount.name = "";
                                }
                                if (!bankAccount.bic) {
                                    bankAccount.bic = "";
                                }
                                if (this.type === "directDebit") {
                                    if (!bankAccount.mandate) {
                                        bankAccount.mandate = {
                                            id: "",
                                            name: "",
                                        };
                                    }
                                }
                            }
                            tpc.bankAccount = bankAccounts[bankAccounts.length - 1];
                            tpc.bankAccounts = bankAccounts;
                        }
                    }

                    this.thirdPartyCompanies = data.companies;
                    this.updateThirdPartyCompaniesBankAccounts();
                } else {
                    this.thirdPartyCompanies = [];
                }
            }
        },
    })
    thirdPartyCompanies: any = [];

    // get thirdPartyCompanies() {
    //     if (this.filteredThirdPartyCompanies) {
    //         return filteredThirdPartyCompanies
    //     }
    //     return this.
    // }

    updateThirdPartyCompaniesBankAccounts() {
        for (const transaction of this.transactions) {
            if (transaction.thirdPartyCompanyId) {
                const foundThirdPartyCompany = this.thirdPartyCompanies.find(
                    (tpc) => transaction.thirdPartyCompanyId === tpc.id,
                );
                if (foundThirdPartyCompany) {
                    if (!transaction.bankAccount?.iban && !transaction.bankAccount?.bic) {
                        transaction.bankAccount = foundThirdPartyCompany.bankAccount;
                    }
                    transaction.bankAccounts = foundThirdPartyCompany.bankAccounts;
                    if (!transaction.thirdPartyCompany) {
                        transaction.thirdPartyCompany = foundThirdPartyCompany;
                    }
                } else {
                    debugger; // git-ignore
                }
            }
        }
    }

    @Apollo({
        query: gql`
            query ($ids: [ID!]) {
                users(where: { id_in: $ids }) {
                    id
                    firstname
                    lastname
                    iban
                    bankAccounts
                }
            }
        `,
        variables() {
            return {
                ids: this.userIds,
            };
        },
        skip() {
            return !this.userIds?.length;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data?.users) {
                    for (const user of data.users) {
                        if (!user.bankAccounts) {
                            user.bankAccounts = [];
                        }

                        if (user.bankAccounts.length) {
                            user.bankAccount = user.bankAccounts[user.bankAccounts.length - 1];
                        } else if (user.iban) {
                            user.bankAccounts.push({
                                iban: user.iban,
                                bic: "",
                                default: true,
                            });
                            user.bankAccount = user.bankAccounts[0];
                        } else {
                            user.bankAccount = {
                                name: "",
                                iban: "",
                                bic: "",
                            };
                        }
                    }

                    this.users = data.users;
                    this.updateUserBankAccounts();
                } else {
                    this.users = [];
                }
            }
        },
    })
    users = [];

    updateUserBankAccounts() {
        for (const transaction of this.transactions) {
            if (transaction.userId) {
                const foundUser = this.users.find((user) => transaction.userId === user.id);
                if (foundUser) {
                    if (!transaction.bankAccount?.iban && !transaction.bankAccount?.bic) {
                        transaction.bankAccount = foundUser.bankAccount;
                    }
                    transaction.bankAccounts = foundUser.bankAccounts;
                    if (!transaction.user) {
                        transaction.user = foundUser;
                    }
                }
            }
        }
    }

    get profileId() {
        return this.$store.state.selected_company?.profile?.id;
    }

    async deleteAssociatedPaymentLinks(transactions) {
        if (this.accountingLines) {
            const paymentLinksAlreadyDeleted = [];
            for (const transaction of transactions) {
                const accLine = this.accountingLines.find(
                    (accL) => accL.id === transaction.accountingLineId,
                );
                if (
                    accLine &&
                    accLine.associatedPaymentLink?.id &&
                    !paymentLinksAlreadyDeleted.includes(accLine.associatedPaymentLink.id)
                ) {
                    await this.$apollo.mutate({
                        mutation: DELETE_PAYMENT_LINK,
                        variables: {
                            id: accLine.associatedPaymentLink.id,
                        },
                    });
                    paymentLinksAlreadyDeleted.push(accLine.associatedPaymentLink.id);
                }
            }
        }
    }

    async updateSepa(status = "ANALYSED") {
        if (!this.selectedSepa) {
            return;
        }
        this.loading = true;
        const data = {
            sepa: {
                type: this.type,
                iban: this.bankAccount.iban,
                bic: this.bankAccount.bic,
                creditorIdentifier: this.bankAccount.creditorIdentifier || undefined,
                bankAccount: this.bankAccount,
                total: this.total,
                totalSigned: this.totalSigned,
                transactions: [],
            },
        };
        for (const transaction of this.transactions) {
            data.sepa.transactions.push({
                accountingLineId: transaction.accountingLineId,
                reason: transaction.reason,
                thirdParty: transaction.thirdParty,
                bankAccount: transaction.bankAccount,
                amountSigned: transaction.amountSigned,
                paymentAmount: transaction.paymentAmount,
                accountableAccountName: transaction.accountableAccountName,
                userId: transaction.userId || undefined,
                thirdPartyCompanyId: transaction.thirdPartyCompanyId || undefined,
            });
        }
        // debugger;
        if (this.type === "directDebit") {
            await this.deleteAssociatedPaymentLinks(this.transactions);
        }
        // debugger
        if (!this.selectedSepa.id) {
            await this.$apollo.mutate({
                mutation: CREATE_DOCUMENT,
                variables: {
                    name: "",
                    displayName: this.selectedSepa.displayName,
                    documentType: "SEPA",
                    date: getDBFormattedDate(this.selectedSepa.date),
                    companyId: this.companyId,
                    useForReconciliation: false,
                    show: true,
                    grossAmount: Math.abs(this.total),
                    grossAmountSigned: this.total,
                    data,
                    status,
                    accountingLinesLocked: false,
                },
            });
        } else {
            await this.$apollo.mutate({
                mutation: UPDATE_DOCUMENT,
                variables: {
                    documentId: this.selectedSepa.id,
                    type: "SEPA",
                    creditNote: false,
                    multiVat: false,
                    displayName: this.selectedSepa.displayName,
                    date: getDBFormattedDate(this.selectedSepa.date),
                    grossAmount: Math.abs(this.total),
                    grossAmountSigned: this.total,
                    data,
                    status,
                },
            });
        }
        const originalBankAccount = this.originalBankAccounts.find(
            (ob) => ob.id === this.bankAccount.id,
        );
        debugger;
        if (originalBankAccount?.id) {
            // iban: this.bankAccount.iban,
            //     bic: this.bankAccount.bic,
            //     creditorIdentifier: this.bankAccount.creditorIdentifier || undefined,
            const data = {
                iban: undefined,
                bic: undefined,
                creditorIdentifier: undefined,
            };
            let update = false;
            if (!originalBankAccount.creditorIdentifier && this.bankAccount.creditorIdentifier) {
                data.creditorIdentifier = this.bankAccount.creditorIdentifier;
                update = true;
            }
            if (originalBankAccount.iban != this.bankAccount.iban) {
                data.iban = this.bankAccount.iban;
                update = true;
            }
            if (originalBankAccount.bic != this.bankAccount.bic) {
                data.bic = this.bankAccount.bic;
                update = true;
            }
            if (update) {
                await this.$apollo.mutate({
                    mutation: UPDATE_BANK_ACCOUNT,
                    variables: {
                        id: this.bankAccount.id,
                        creditorIdentifier: data.creditorIdentifier,
                        iban: data.iban,
                        bic: data.bic,
                    },
                });
            }
        }
        this.$emit("onUpdated");
        this.loading = false;
    }

    get rules() {
        return {
            notEmpty: (value) => {
                return !!value || this.$t("settings.itemRequired");
            },
            checkSiret: (value) => {
                return isSiret(value) || this.$t("settings.wrongSiret");
            },
            checkIBAN: (value) => {
                return checkIBAN(value) || this.$t("settings.wrongIBAN");
            },
            checkBIC: (value) => {
                return checkBIC(value) || this.$t("settings.wrongBIC");
            },
            checkICS: (value) => {
                return checkICS(value) || this.$t("settings.wrongICS");
            },
        };
    }

    onIbanChanged(bankAccount) {
        onIbanChanged(bankAccount);
    }

    cleanWord(word) {
        const cw = word.replace(/ {1,}/g, "");
        return cw;
    }

    removeTransaction(transaction) {
        const index = this.transactions.findIndex((t) => t === transaction);
        if (index !== -1) {
            this.transactions.splice(index, 1);
        }
    }
}

export default toNative(Sepa);
</script>
